import type { ComponentProps } from "react"
import { useMemo, useState } from "react"
import * as R from "remeda"

import type { IconType, ProjectIconKeys } from "@productlane/ui"
import type {
	Company,
	Contact,
	CustomerNeed,
	Feedback,
	Issue,
	LinearRoadmap,
	Project,
	Segment,
	Tag,
} from "@productlane/zero"
import { sortByCustomOrder } from "@productlane/lib/src/roadmap-custom-order"
import { ProjectIcons } from "@productlane/ui"

import type { RoadmapAugmentedIssue } from "./issues"
import { EMOJIS } from "./emojis"
import { useFilteredProjects } from "./use-team-filter"
import { useZeroWorkspace } from "./zero"

export type RoadmapAugmentedFeedback = Feedback & {
	tags: readonly Tag[]
	projects: readonly Project[]
	issues: readonly Issue[]
	contact: (Contact & { segments: readonly Segment[] }) | undefined
	company: (Company & { segments: readonly Segment[] }) | undefined
}

export type RoadmapAugmentedProject = Project & {
	customerNeeds: readonly CustomerNeed[]
	linearRoadmaps: readonly LinearRoadmap[]
	issues: readonly RoadmapAugmentedIssue[]
}

export type ProjectResult = Project & {
	linearRoadmaps: readonly LinearRoadmap[]
	customerNeeds: readonly CustomerNeed[]
}

export function getProjectIcon(icon: string | null | undefined): IconType {
	if (!icon) {
		return ProjectIcons.Cube
	} else if (icon.startsWith(":")) {
		const emoji = EMOJIS[icon]
		return emoji ? emojiIcon(emoji) : ProjectIcons.Cube
	} else {
		const projectIcon = ProjectIcons[icon as ProjectIconKeys]
		if (!projectIcon) {
			return ProjectIcons.Cube
		}
		return projectIcon
	}
}

/** An emoji drawn in the icon's own box, so it sizes like every other project icon. */
function emojiIcon(emoji: string): IconType {
	return function EmojiIcon(props: ComponentProps<"svg">) {
		return (
			<svg width="16" height="16" viewBox="0 0 16 16" {...props}>
				<text
					x="50%"
					y="50%"
					fontSize="13"
					textAnchor="middle"
					dominantBaseline="central"
				>
					{emoji}
				</text>
			</svg>
		)
	} as IconType
}

export function useSortedProjects({
	projects,
}: {
	projects: readonly RoadmapAugmentedProject[]
}) {
	const [maxMap, setMaxMap] = useState(new Map<string, number>())
	const workspace = useZeroWorkspace()
	const filteredProjects = useFilteredProjects(projects)

	const sortedProjects = useMemo(() => {
		const maxValues = new Map<string, number>()
		for (const project of filteredProjects) {
			if (project.state && !maxValues.has(project.state)) {
				maxValues.set(project.state, 0)
			}
			maxValues.set(
				project.state ?? "",
				Math.max(project.totalScore, maxValues.get(project.state ?? "") ?? 0),
			)
		}
		setMaxMap(maxValues)

		const sortOrder = workspace?.roadmapSettings?.sortOrder ?? "SCORE_DESC"

		switch (sortOrder) {
			case "LINEAR":
				return R.sortBy(filteredProjects, [(p) => p.sortOrder, "asc"])
			case "LINEAR_REVERSE":
				return R.sortBy(filteredProjects, [(p) => p.sortOrder, "desc"])
			case "SCORE_ASC":
				return R.sortBy(filteredProjects, [(p) => p.totalScore, "asc"])
			case "CUSTOM":
				return sortByCustomOrder(
					filteredProjects,
					(p) => p.portalSortOrder,
					(p) => p.totalScore,
				)
			case "SCORE_DESC":
			default:
				return R.sortBy(filteredProjects, [(p) => p.totalScore, "desc"])
		}
	}, [filteredProjects, workspace?.roadmapSettings?.sortOrder])

	return {
		maxMap,
		sortedProjects,
	}
}

const projectStatusOrder = [
	"In Progress",
	"Planned",
	"Paused",
	"Backlog",
] as const

export function useCategorizedProjects(
	projects: readonly Project[],
): Record<string, Project[]> {
	return useMemo(() => {
		if (!projects || projects.length === 0)
			return {} as Record<string, Project[]>

		const sortByScore = (items: Project[]) =>
			R.sortBy(items, [(p) => p.totalScore ?? 0, "desc"])

		const categorizeProjects = (state: string) =>
			sortByScore(projects.filter((p) => p.state === state))

		const categories: Record<string, Project[]> = {
			"In Progress": categorizeProjects("started"),
			Planned: categorizeProjects("planned"),
			Paused: categorizeProjects("paused"),
			Backlog: categorizeProjects("backlog"),
		}

		const orderedResult: Record<string, Project[]> = {}
		for (const status of projectStatusOrder) {
			if (categories[status] && categories[status].length > 0) {
				orderedResult[status] = categories[status]
			}
		}

		return orderedResult
	}, [projects])
}
